'use client';

import { useEffect, useMemo, useState } from 'react';

import { IndustrySelectField } from '@/features/workspaces/components/selectors';
import { useUpdateCurrentWorkspace } from '@/features/workspaces/hooks';
import { updateCurrentWorkspaceSchema } from '@/features/workspaces/schemas';
import { useAppSelector } from '@/store/hooks';
import { useWizardBrowserStorage } from '@web/hooks';
import { MainButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify, zodErrorsToFieldErrors } from '@web/utils';

import type {
  UpdateCurrentWorkspacePayload,
  WorkspaceIndustryOption,
} from '@/features/workspaces/types';

type WorkspaceSetupFormState = {
  name: string;
  slug: string;
  industry_id: string;
  industry: WorkspaceIndustryOption | null;
  primary_domain: string;
  logo_asset_key: string;
};

type Props = {
  isOnboarding?: boolean;
  onSuccess?: () => void;
};

const onboardingIntroCopy =
  "A workspace in Net Net is your team's shared operating environment. It's the place where the same Jobs, Tasks, Deliverables, Time, and Chat live for everyone on that team, along with the settings, roles, and rules that shape how the team works. If you belong to more than one team, you can be in multiple workspaces, each with its own people, structure, and permissions." as const;

export function WorkspaceSetupForm({ isOnboarding, onSuccess }: Props) {
  const { currentUser, currentWorkspaceSlug } = useAppSelector((s) => s.auth);
  const { currentWorkspace } = useAppSelector((s) => s.workspaces);
  const currentWorkspaceWithDomains = currentWorkspace as
    | (typeof currentWorkspace & {
        domains?: { domain: string }[] | null;
        industry?: WorkspaceIndustryOption | null;
      })
    | null;

  const wizardId = `${currentUser?.email || 'anon'}:${currentWorkspaceSlug || 'no-ws'}`;
  const wizard = useWizardBrowserStorage({
    wizardId,
    version: 1,
    storage: 'local',
  });

  const persisted = wizard.getStepData<WorkspaceSetupFormState>('workspace');

  const [form, setForm] = useState<WorkspaceSetupFormState>(
    () =>
      persisted ?? {
        name: currentWorkspace?.name || '',
        slug: currentWorkspace?.slug || '',
        industry_id: currentWorkspaceWithDomains?.industry?.id || '',
        industry: currentWorkspaceWithDomains?.industry || null,
        primary_domain:
          currentWorkspaceWithDomains?.domains?.[0]?.domain ||
          currentWorkspace?.primary_domain ||
          currentWorkspace?.domain ||
          '',
        logo_asset_key: currentWorkspace?.logo_url || '',
      },
  );

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const { update, isLoading } = useUpdateCurrentWorkspace();

  const isValid = useMemo(() => {
    const res = updateCurrentWorkspaceSchema.safeParse(form);
    return res.success;
  }, [form]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = updateCurrentWorkspaceSchema.safeParse(form);

    if (!parsed.success) {
      setErrors(zodErrorsToFieldErrors<WorkspaceSetupFormState>(parsed.error));
      return;
    }

    try {
      const normalizedDomain = parsed.data.primary_domain.trim().toLowerCase();
      const payload: UpdateCurrentWorkspacePayload = {
        name: parsed.data.name,
        slug: parsed.data.slug,
        industry_id: parsed.data.industry_id,
        logo_asset_key: parsed.data.logo_asset_key,
        ...(normalizedDomain
          ? { domains: [{ domain: normalizedDomain }] }
          : {}),
      };

      const res = await update(payload);
      if ((res as any).success) {
        onSuccess?.();
      }
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      wizard.saveStep('workspace', form);
    }, 400);
    return () => clearTimeout(t);
  }, [form, wizard]);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
      {isOnboarding ? (
        <div className="card-container-glass">{onboardingIntroCopy}</div>
      ) : null}

      <SmartInput
        type="text"
        name="name"
        label="Workspace Name"
        value={form.name}
        onChange={(v) => setForm((f) => ({ ...f, name: v }))}
        onValidateChange={(_, err) =>
          setErrors((e) => ({ ...e, name: err || undefined }))
        }
        schema={updateCurrentWorkspaceSchema.shape.name}
        error={errors.name}
      />

      <IndustrySelectField
        value={form.industry}
        onChange={(value) => {
          setForm((f) => ({
            ...f,
            industry_id: value?.id || '',
            industry: value,
          }));
          if (errors.industry_id) {
            setErrors((e) => ({ ...e, industry_id: undefined }));
          }
        }}
        error={errors.industry_id}
      />

      <SmartInput
        type="text"
        name="slug"
        label="Workspace Slug"
        value={form.slug}
        onChange={(v) => setForm((f) => ({ ...f, slug: v }))}
        onValidateChange={(_, err) =>
          setErrors((e) => ({ ...e, slug: err || undefined }))
        }
        schema={updateCurrentWorkspaceSchema.shape.slug}
        error={errors.slug}
      />

      <SmartInput
        type="text"
        name="primary_domain"
        label="Primary Domain"
        value={form.primary_domain}
        onChange={(v) => setForm((f) => ({ ...f, primary_domain: v }))}
        onValidateChange={(_, err) =>
          setErrors((e) => ({ ...e, primary_domain: err || undefined }))
        }
        schema={updateCurrentWorkspaceSchema.shape.primary_domain}
        error={errors.primary_domain}
      />

      {!isOnboarding && (
        <SmartInput
          type="text"
          name="logo_asset_key"
          label="Logo Asset Key"
          value={form.logo_asset_key}
          onChange={(v) => setForm((f) => ({ ...f, logo_asset_key: v }))}
          onValidateChange={(_, err) =>
            setErrors((e) => ({ ...e, logo_asset_key: err || undefined }))
          }
          schema={updateCurrentWorkspaceSchema.shape.logo_asset_key}
          error={errors.logo_asset_key}
        />
      )}

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
        <MainButton type="submit" isLoading={isLoading} disabled={!isValid}>
          Save & Continue
        </MainButton>
      </div>
    </form>
  );
}
